import * as PIXI from "pixi.js";
import {
  symbolData,
  BLANK_SYMBOL_ID,
  COIN_JACKPOT_SYMBOL_ID,
  isCoinSymbol,
} from "../../SymbolData";
import { Settings } from "../../settings";
import { FEATURES_CONFIG } from "../../featuresConfig";
// NOT USED since the cell formats through coinValueLabel (cached):
// import { formatToCustomLocale } from "../../utilities";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";
import { PROVIDER_CONFIG } from "../../RGS/Providers/PopiPlay/providerConfig";
import store from "../../State/Store/store";
import { createCoinLabel, setCoinLabel } from "./coinLabel";
import { COIN_LABEL_MAX_WIDTH_RATIO } from "../Shared/coinValueLabel";
import { cosmeticCoinValue } from "../Shared/coinValues";
import { formatCoinValue, warmCoinValueLabels } from "../Shared/coinValueLabel";

/**
 * HoldAndWinCellController — one cell of the Cleopatra's Fire Hold & Win grid.
 *
 * The motion is ReelControllerV2's, for one visible symbol: the velocity-
 * matched stop, BackOut easing, the landing peak, ticker-deltaMS timing that
 * freezes with the game, deferred resize, and a per-frame movement cap. No
 * wind-up. Config: FEATURES_CONFIG.HOLD_AND_WIN_SPIN. Also:
 *
 *   - Resize guard (from ReelControllerV2.resizeSymbol): a 0 / NaN size is
 *     ignored instead of poisoning every sprite position with NaN — which left
 *     the cell never landing and the round hung.
 *   - Landing sound at the peak (ReelControllerV2's moment): coin_land for
 *     anything that is not a blank, once per frame across the whole grid.
 *   - Lightweight for 40 cells:
 *       * textures come from a table built once, not a "name.png" lookup per
 *         wrap;
 *       * coins scrolling past carry a random value from the GDD's coin
 *         multipliers (paytable: 0.5x, 1x, 1.5x, 2x, 3x, 4x, 5x of the total
 *         bet); the LANDING coin prints the server's value;
 *       * sprites outside the cell window are culled (renderable = false), so
 *         only the one or two that can show are drawn.
 *
 * The landing value is pushed by the owner (setLandingValue) — it comes from
 * the server's grid, never from here.
 *
 * ReelControllerV2 formula (N = visible symbol count), N = 1:
 *   SPRITE_COUNT = N*2 + 2 = 4, VISIBLE_SYMBOL_INDEX = 1 + N = 2,
 *   STOP_MOVE_DISTANCE = N = 1.
 *   [0: top buffer, 1: landing zone, 2: VISIBLE, 3: bottom buffer]
 */

const SPRITE_COUNT = 4;
const VISIBLE_SYMBOL_INDEX = 2;
const STOP_MOVE_DISTANCE = 1;

// ── The spin strip: everything that can land in a cell ──────────────────────
// Coins (the server's coin id, 9), chest, golden chest and scarab — the cell
// kinds of bonus_data — over blanks. A blank draws nothing (SymbolData 11 has
// no texture), so the strip shows symbols passing over the plate. The jackpot
// coin is not in the strip: its art belongs to the value the server names.
//
// Weights are cosmetic (the server decides what lands): mostly blanks, coins
// often, the specials — the back end's every-4th-cell special table — rarer.
const HNW_COIN_IDS = PROVIDER_CONFIG.HOLD_AND_WIN_TRIGGER?.COIN_IDS ?? [9];
const HNW_STRIP_WEIGHTS = [
  { id: HNW_COIN_IDS[0], weight: 0.25 }, // coin
  { id: 12, weight: 0.05 }, // chest
  { id: 13, weight: 0.03 }, // golden chest
  { id: 14, weight: 0.05 }, // scarab
  // the rest (0.62): blank
];

// The values a spinning coin shows, as multiples of the total bet — the GDD's
// coin multipliers (paytable, "Coin Symbols"), which are also the contract's
// common coin values (options.coin_values 10..100 points).
// NOT USED since the shared set (coinValues.COIN_MULTIPLIERS) — same numbers:
// const HNW_SPIN_COIN_MULTIPLIERS = [0.5, 1, 1.5, 2, 3, 4, 5];

/** The stake the values are shown against — the free-rounds bet when active. */
const currentBet = () => {
  const state = store.getState();
  const freeRoundsActive = state?.freeRounds?.status === "active";
  const freeRoundsBet = state?.freeRounds?.betValue || null;
  return (freeRoundsActive && freeRoundsBet) || state?.game?.bet || 1;
};

class HoldAndWinCellController {
  constructor(
    spinningContainer,
    symbolId,
    symbolWidth,
    symbolHeight,
    symbolIndex,
    reelIndex,
    onStopCallback = null,
    symbolsSpriteSheet = null,
  ) {
    this.spinningContainer = spinningContainer;
    this.symbolId = symbolId;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolIndex = symbolIndex;
    this.reelIndex = reelIndex;
    this.onStopCallback = onStopCallback;
    this.onLandingPeak = null; // Callback at overshoot trough (before full settle)
    this.spritesheet = symbolsSpriteSheet;

    // Animation state
    this.isPlaying = false;
    this.hasStopped = false;
    this.isStopping = false;
    this.isWindingUp = false;
    this.tickerCallback = null;
    this._windUpTickerFn = null;
    this.spinningSprites = [];
    // Truthy while a spin/stop cycle is active (timing uses _spinElapsed, not wall clock)
    this.spinStartTime = null;
    this._spinElapsed = 0; // Accumulated spin time (ms) — advances only when ticker runs
    this.normalizedSpinSpeed = 0;
    this.lastCalculatedPosition = 0;
    this.stopTweenActive = false;

    // Premium feel: acceleration
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;

    // Landing data from API
    this.pendingLandingSymbolId = null;

    // Ticker-based stop params (replaces anime.js / performance.now)
    this._stopElapsed = 0; // Accumulated stop animation time (ms)
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this._landingPeakFired = false;

    // Speed mode — set per-spin by the engine before calling startSpin()
    this.autoPlaySpeed = "auto";
    this._speedConfigCache = null;

    // The value the landing coin prints — pushed by the owner per spin.
    this.landingValue = null;
    // symbolId -> PIXI.Texture, built once per spritesheet.
    this._textureTable = null;

    // Tension deceleration state (ported from ReelControllerV2)
    this.isTensionMode = false;
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0; // Accumulated tension deceleration time (ms)
    this._tensionDecelerationDuration = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Deferred resize state — ported from ReelControllerV2.
    // When spinning/stopping/winding-up, resize is deferred to safe points
    // (wrap boundary, finalize landing, next spin start) to prevent
    // corrupted stop easing distances and sprite position desync.
    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = symbolHeight;
    this._targetSymbolHeight = symbolHeight;
    this._targetSymbolWidth = symbolWidth;

    // Caches
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache = new Map();

    this._createSpinningSprites();
    this.spinningContainer.roundPixels = true;
    // Start hidden — spinningContainer.alpha controls visibility, not individual sprite.visible.
    // This prevents random textures from leaking through transparent symbols (e.g. blanks).
    this.spinningContainer.alpha = 0;
  }

  // ── Speed Config ───────────────────────────────────────────────────────

  /**
   * Returns the speed config for bonus spins.
   * Hold & Win runs at one speed — FEATURES_CONFIG.HOLD_AND_WIN_SPIN.
   */
  _getSpeedConfig() {
    if (this._speedConfigCache) {
      return this._speedConfigCache;
    }

    const config = FEATURES_CONFIG.HOLD_AND_WIN_SPIN;
    this._speedConfigCache = config;

    return config;
  }

  // ── Sprite Creation (from ReelControllerV2) ────────────────────────────

  _createSpinningSprites() {
    if (!this.spinningContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      const normalSprite = new PIXI.Sprite();
      normalSprite.anchor.set(0.5);
      normalSprite.name = "spinning_symbol_texture";
      normalSprite.alpha = 1;
      normalSprite.roundPixels = true;
      symbolContainer.addChild(normalSprite);

      let valueText = null;
      if (HNW_COIN_IDS.length > 0) {
        // The coin number: a Sprite from the label cache (see coinLabel.js) —
        // crisp at the renderer's density, never rounded, one texture per value.
        valueText = createCoinLabel();
        valueText.name = "symbol_value";
        valueText.zIndex = 1;
        symbolContainer.addChild(valueText);
      }

      this.spinningSprites.push({
        container: symbolContainer,
        normalSprite,
        valueText,
        currentSymbolId: null,
      });

      this.spinningContainer.addChild(symbolContainer);
    }

    // Initialize with random textures
    this._initializeSpinningTextures();

    // Sprites are always visible — the parent container's mask
    // (set in HoldAndWinCell.jsx) clips to 1 symbolHeight,
    // so only the sprite at the visible position shows through.
  }

  _initializeSpinningTextures() {
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === VISIBLE_SYMBOL_INDEX) {
        // Visible sprite matches the cell's actual symbol so it doesn't
        // leak a random texture through transparent symbols (e.g. blank).
        this._setSpriteTexture(this.spinningSprites[i], this.symbolId);
      } else {
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[i], randomId);
      }
    }
  }

  // ── Random Symbol Generation ──────────────────────────────────────────

  // One weighted draw from the strip table; blank for the remainder. No state
  // read per wrap.
  _generateRandomSymbolId() {
    let roll = Math.random();
    for (const { id, weight } of HNW_STRIP_WEIGHTS) {
      if (roll < weight) return symbolData[id] ? id : BLANK_SYMBOL_ID;
      roll -= weight;
    }
    return BLANK_SYMBOL_ID;
  }

  /** symbolId -> texture, from a table built once (not a string per wrap). */
  _textureFor(symbolId) {
    if (!this._textureTable) {
      this._textureTable = new Map();
      const textures = this.spritesheet?.textures || {};
      for (const [id, info] of Object.entries(symbolData)) {
        if (info?.texture && textures[`${info.texture}.png`]) {
          this._textureTable.set(Number(id), textures[`${info.texture}.png`]);
        }
      }
    }
    return this._textureTable.get(symbolId) || null;
  }

  /** The value the next landing coin prints (from the server's grid). */
  setLandingValue(value) {
    this.landingValue = value ?? null;
  }

  // ── Coin Display Value (from ReelControllerV2) ─────────────────────────

  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseFloat(symbolValue);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Values are already in subunits (pre-calculated by server or
      // normalized by RGS adapter) — just format, no bet multiplication.
      // Cached (coinValueLabel): a wrapping cell does not re-format.
      // return formatToCustomLocale(coinValue);
      return formatCoinValue(coinValue);
    } catch (error) {
      return "";
    }
  }

  // ── Symbol Scale (from ReelControllerV2) ───────────────────────────────

  // Calculates uniform symbol scale based on standard symbol reference height.
  // All symbols get the SAME scale factor — premium symbols with larger sourceSize
  // naturally overflow their cells, preserving the intended size relationships.
  // Cached per container dimensions for performance (scale is symbol-independent).
  _calculateSymbolScale(containerWidth, containerHeight, symbolId) {
    if (!containerHeight) return 1;

    // Look up symbolPay for padding skip
    const info = symbolId != null ? symbolData[symbolId] : null;
    const symbolPay = info?.symbolPay || '';

    // Check cache — key includes symbolPay since feature symbols skip padding
    const cacheKey = `uniform_${containerWidth}_${containerHeight}_${symbolPay}`;
    if (this._nativeDimensionsCache.has(cacheKey)) {
      return this._nativeDimensionsCache.get(cacheKey);
    }

    // Same per-category multiplier as the base reels (Settings
    // .SYMBOL_SCALE_OVERRIDES), so a coin is the same art size everywhere.
    const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[symbolPay]
      ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
      ?? 1;
    const scale = (containerHeight / Settings.SYMBOL_HEIGHT) * categoryScale;

    // Cache result
    this._nativeDimensionsCache.set(cacheKey, scale);

    return scale;
  }

  // ── Texture Management (from ReelControllerV2) ─────────────────────────

  // Sets sprite texture and coin value text
  _setSpriteTexture(spriteData, symbolId) {
    // Same id, same picture — nothing to do. Coins always re-apply (a new
    // value each time), and so does the landing sprite.
    if (
      spriteData.currentSymbolId === symbolId &&
      !isCoinSymbol(symbolId) &&
      !spriteData._isLandingSprite
    ) {
      return;
    }

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      setCoinLabel(spriteData.valueText, "");
      spriteData.currentSymbolId = null;
      return;
    }

    spriteData.currentSymbolId = symbolId;
    const textureName = symbolInfo.texture;
    // The LANDING coin prints the server's value, pushed by the owner. Coins
    // scrolling past show a random GDD coin value against the current bet.
    let symbolValue = symbolInfo.value;
    if (isCoinSymbol(symbolId)) {
      symbolValue = spriteData._isLandingSprite
        ? (this.landingValue ?? symbolInfo.value)
        : // The shared cached set (coinValues) — the same seven values the
          // base reels spin, already pre-drawn at spin start.
          // HNW_SPIN_COIN_MULTIPLIERS[
          //   Math.floor(Math.random() * HNW_SPIN_COIN_MULTIPLIERS.length)
          // ] * currentBet();
          cosmeticCoinValue(currentBet());
    }

    // If texture is null or empty, set empty textures and skip processing
    if (!textureName || textureName === null) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      setCoinLabel(spriteData.valueText, "");
      spriteData.currentSymbolId = null;
      return;
    }

    if (this.spritesheet && textureName) {
      spriteData.normalSprite.texture =
        this._textureFor(symbolId) || PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 1;
    }

    const scale = this._calculateSymbolScale(
      this.symbolWidth,
      this.symbolHeight,
      symbolId,
    );
    if (spriteData.normalSprite) {
      spriteData.normalSprite.scale.set(scale);
    }

    // The coin number — coins only. Centred on the coin (anchor 0.5, y = 0),
    // like the spine's txt_slot_coin; squeezed to 90% of the cell if wider.
    // Never on the JACKPOT coin: its tier word (MINI … GRAND) is its face,
    // and a number under it is not how it reads (Vladimir, 2026-09-29).
    const label =
      symbolValue && isCoinSymbol(symbolId) && symbolId !== COIN_JACKPOT_SYMBOL_ID
        ? this._getCoinDisplayValue(symbolValue)
        : "";
    setCoinLabel(
      spriteData.valueText,
      label,
      this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER,
      // this.symbolWidth * 0.9,
      this.symbolHeight * COIN_LABEL_MAX_WIDTH_RATIO, // the spine's ratio (HoldAndWinOverlayCell)
    );
  }

  // ── Start Spin ────────────────────────────────────────────────────────

  startSpin() {
    if (this.isPlaying) {
      return;
    }
    // The labels this cell can spin past, pre-drawn once per bet and size.
    const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
    const warmKey = `${currentBet()}|${Math.round(fontSize)}`;
    if (this._coinWarmKey !== warmKey) {
      this._coinWarmKey = warmKey;
      warmCoinValueLabels(fontSize);
    }

    // Apply any deferred resize before starting the spin
    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.accelerationMultiplier = 0; // Ramp from 0
    this.currentVelocity = 0;
    // pendingLandingSymbolId is cleared by the engine before startSpin() is
    // called. New API data arrives via setLandingSymbolId() after rgsPlay().

    // Reset stop params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    // Reset tension state
    this._resetTensionState();

    // Calculate spin speed from speed-dependent config
    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    this.spinStartTime = true; // sentinel — timing uses _spinElapsed
    this._spinElapsed = 0;
    this.lastCalculatedPosition = 0;

    // Reset sprite positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Sprites are always visible — mask handles clipping
    // Show the spinning container (hidden at rest to prevent texture leak-through)
    this.spinningContainer.alpha = 1;

    // Set visible sprite (index 2) to current symbol so it visually pushes down,
    // other sprites get random textures for the spinning animation
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === VISIBLE_SYMBOL_INDEX) {
        this._setSpriteTexture(this.spinningSprites[i], this.symbolId);
      } else {
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[i], randomId);
      }
    }

    // Start ticker loop
    this.tickerCallback = () => {
      if (this.spinStartTime === null || this.hasStopped) return;
      this.updateSymbolsAbsolute();
    };
    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  // ── Start Spin No Wind-Up (TURBO — instant full speed) ────────────

  /**
   * Start spinning at full speed with no acceleration ramp.
   * Used by the turbo wait-for-server drop pattern:
   *   API responds → startSpinNoWindUp() → immediately stopSpin()
   * Symbols only travel the configured stop distance before landing.
   */
  startSpinNoWindUp() {
    if (this.isPlaying) return;

    // Apply any deferred resize before starting the spin
    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.accelerationMultiplier = 1.0; // Full speed immediately — no ramp
    this.currentVelocity = 1.0;

    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    this.spinStartTime = true; // sentinel — timing uses _spinElapsed
    this._spinElapsed = 0;
    this.lastCalculatedPosition = 0;

    // Reset sprite positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    this.spinningContainer.alpha = 1;

    // Set visible sprite to current symbol, others get random textures
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === VISIBLE_SYMBOL_INDEX) {
        this._setSpriteTexture(this.spinningSprites[i], this.symbolId);
      } else {
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[i], randomId);
      }
    }

    // Start ticker loop
    this.tickerCallback = () => {
      if (this.spinStartTime === null || this.hasStopped) return;
      this.updateSymbolsAbsolute();
    };
    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  // No wind-up in Hold & Win (Vladimir, 2026-09-28): cells leave on the
  // press, as the base reels do with SKIP_WIND_UP. startSpinWithWindUp was
  // removed from this copy; the isWindingUp guards below stay inert.

  // ── Stop Spin ─────────────────────────────────────────────────────────

  stopSpin(isManualStop = false) {
    if (this.isStopping || this.hasStopped) {
      return;
    }
    // Allow stop during spinning OR wind-up.
    // During wind-up: isStopping flag is set now, and when wind-up completes
    // and the spin ticker starts, it'll execute the stop on the first wrap.
    if (!this.isPlaying && !this.isWindingUp) {
      return;
    }
    this._isManualStop = isManualStop;
    this.isStopping = true;
  }

  /**
   * Turbo stop — bypass the wrap-wait and execute stop easing immediately.
   * Normal stop waits for a sprite to wrap before executing _calculateAndExecuteStop,
   * which means the symbol can travel an entire cycle. Turbo needs instant stop
   * from the current position with extra travel distance for visible drop.
   */
  stopSpinImmediate() {
    if (this.hasStopped || this.stopTweenActive) return;
    // During a wind-up the spin has not started yet: remember the stop, and
    // the first wrap after the release executes it (as stopSpin does).
    if (this.isWindingUp) {
      this.isStopping = true;
      return;
    }
    if (!this.isPlaying) return;

    this.isStopping = true;

    // Execute stop easing NOW — don't wait for wrap
    if (this.pendingLandingSymbolId !== null) {
      this._calculateAndExecuteStop();
    }
  }

  /**
   * Set the symbol ID that should appear when this symbol stops.
   * Called by the engine when API response arrives.
   * Do NOT paint textures here — only _setLandingTexture at stop start may
   * write the landing id (painting earlier scrolls into the visible strip).
   */
  setLandingSymbolId(symbolId) {
    this.pendingLandingSymbolId = symbolId;
  }

  // ── Main Animation Loop (from ReelControllerV2) ───────────────────────

  updateSymbolsAbsolute() {
    if (this.spinStartTime === null || this.hasStopped) return;

    // ── Frame-start: apply any deferred resize immediately ──
    // This ensures resize applies within 1 frame (~16ms) regardless of which
    // animation phase we're in (normal spin, stop tween, tension, etc.).
    // Previously this only fired at specific safe points (wrap boundary, stop
    // easing branch) which missed controllers in transitional states.
    if (this._pendingPositionUpdate) {

      // If stop easing is active, rescale stop distance proportionally
      // so the easing lands at the correct position at the new height.
      if (this.stopTweenActive && this._stopDuration > 0) {
        const scaleRatio = this._targetSymbolHeight / this.symbolHeight;
        if (Math.abs(scaleRatio - 1.0) > 0.0001) {
          this._stopDistance *= scaleRatio;
          this._stopLastEasedPos *= scaleRatio;
          // Pixels per ms at both ends of a Hermite landing move with height.
          this._stopEntryPx = (this._stopEntryPx || 0) * scaleRatio;
          this._stopExitPx = (this._stopExitPx || 0) * scaleRatio;
        }
      }
      this._applyPendingResize();
    }

    // ── Ticker-based stop animation (BackOut easing inside same ticker) ──
    if (this.stopTweenActive && this._stopDuration > 0) {
      this._stopElapsed += PIXI.Ticker.shared.deltaMS;
      const elapsed = this._stopElapsed;
      const t = Math.min(elapsed / this._stopDuration, 1.0);

      let easedPosition;
      if (this._stopHermite) {
        // Cubic Hermite with a velocity at BOTH ends (see the stop setup):
        //   p(t) = (t³ - 2t² + t)m0 + (-2t³ + 3t²)D + (t³ - t²)m1
        // m0 / m1 are the tangents in distance per unit t (px/ms × duration).
        const t2 = t * t;
        const t3 = t2 * t;
        const m0 = this._stopEntryPx * this._stopDuration;
        const m1 = this._stopExitPx * this._stopDuration;
        easedPosition =
          (t3 - 2 * t2 + t) * m0 +
          (-2 * t3 + 3 * t2) * this._stopDistance +
          (t3 - t2) * m1;
      } else {
        // BackOut easing: f(t) = (t-1)² × ((s+1)(t-1) + s) + 1
        const s = this._stopOvershoot;
        const t1 = t - 1;
        const easedT = t1 * t1 * ((s + 1) * t1 + s) + 1;
        easedPosition = this._stopDistance * easedT;
      }
      const delta = easedPosition - this._stopLastEasedPos;
      this._stopLastEasedPos = easedPosition;

      if (Math.abs(delta) > 0.0001) {
        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        // Wrap check during stop
        const wrapThreshold = this._getWrapThreshold();
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      }

      // Detect overshoot peak (trough) — lowest visual point before bounce-back.
      // Ported from ReelControllerV2: t_peak = 1 - 2s / (3(s+1))
      if (!this._landingPeakFired && this._stopOvershoot > 0) {
        const sPeak = this._stopOvershoot;
        const tPeakMath = 1 - (2 * sPeak) / (3 * (sPeak + 1));
        const peakOffset = this._getSpeedConfig()?.LANDING_PEAK_OFFSET ?? 1.0;
        const tPeak = tPeakMath * peakOffset;
        if (t >= tPeak) {
          this._landingPeakFired = true;
          this._playLandingSound();
          if (this.onLandingPeak) {
            this.onLandingPeak(this.reelIndex, this.symbolIndex);
          }
        }
      }

      this._cullOffscreen();

      if (t >= 1.0) {
        this._finalizeLanding();
      }
      return;
    }

    // ── Normal spin loop — accumulate ticker time (freezes when tab hidden) ──
    this._spinElapsed += PIXI.Ticker.shared.deltaMS;
    const elapsedTime = this._spinElapsed / 1000;
    const currentExpectedPosition = elapsedTime * this.normalizedSpinSpeed;

    let movement = currentExpectedPosition - this.lastCalculatedPosition;
    this.lastCalculatedPosition = currentExpectedPosition; // Always track at full speed

    // --- Tension deceleration (smooth slowdown for anticipation) ---
    // Scales the per-frame visual movement while keeping position tracking at full speed.
    // Ported from ReelControllerV2 — same easing and math.
    if (this._tensionDecelerating) {
      const tensionDecel = FEATURES_CONFIG.TENSION.REEL_DECELERATION;
      this._tensionDecelElapsed += PIXI.Ticker.shared.deltaMS;
      const tensionElapsed = this._tensionDecelElapsed;
      const tDecel = Math.min(tensionElapsed / this._tensionDecelerationDuration, 1.0);
      // Ease-out cubic: starts slowing immediately, arrives at target speed gradually.
      const t1 = 1 - tDecel;
      const easedDecel = 1 - (t1 * t1 * t1);
      this._tensionSpeedMultiplier = 1.0 - (1.0 - tensionDecel.TARGET_SPEED) * easedDecel;

      // Scale visual movement only — position tracker stays at full speed
      movement *= this._tensionSpeedMultiplier;
    }

    // Acceleration ramp (ease-out cubic: fast start, smooth arrival at full speed)
    const speedConfig = this._getSpeedConfig();
    // Support ratio-based (ACCEL_SPEED_RATIO × SPEED_MS) or legacy absolute (ACCELERATION_DURATION)
    const accelDuration = speedConfig.ACCELERATION_DURATION
      ?? (speedConfig.ACCEL_SPEED_RATIO
        ? speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT * speedConfig.ACCEL_SPEED_RATIO
        : 0);
    if (accelDuration > 0 && this.accelerationMultiplier < 1.0) {
      const elapsedMs = this._spinElapsed;
      const accelT = Math.min(elapsedMs / accelDuration, 1.0);
      // Ease-out cubic: starts fast, smoothly reaches 1.0
      this.accelerationMultiplier =
        1 - (1 - accelT) * (1 - accelT) * (1 - accelT);
    } else {
      this.accelerationMultiplier = 1.0;
    }

    // Apply acceleration multiplier
    movement *= this.accelerationMultiplier;

    // ── Frame-spike movement cap ──
    // During resize, React re-renders the entire component tree in the same rAF
    // frame (730ms+). Without a cap, the position calc includes this dead time as
    // movement → sprites overshoot multiple wrap thresholds → infinite spin.
    // Cap at 0.95× symbolHeight — guarantees at most ONE wrap per frame.
    const maxMovement = this.symbolHeight * 0.95;
    if (movement > maxMovement) {
      movement = maxMovement;
    }

    // Track velocity for reference (no blur — just state tracking)
    this.currentVelocity = this.accelerationMultiplier;

    const wrapThreshold = this._getWrapThreshold();
    const shouldUpdateTexture = !this.isStopping && !this.hasStopped;

    let shouldExecuteStop = false;

    // Phase 1: Apply movement to ALL sprites uniformly (no break-and-snap)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y += movement;
    }

    // Phase 2: Check for wraps AFTER all sprites have moved
    // Use while loop (not if) to handle multiple wraps in case any edge case
    // gets past the movement cap (e.g. manual position manipulation, rounding).
    const lastIndex = this.spinningSprites.length - 1;
    while (this.spinningSprites[lastIndex].container.y >= wrapThreshold) {

      this._moveLastSymbolOnTop();

      if (shouldUpdateTexture) {
        // Update texture on the sprite that just wrapped to top (now index 0)
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[0], randomId);
      }

      if (this.isStopping && !this.hasStopped && !this.stopTweenActive) {
        // Only stop when we have landing data from the API
        // (prevents landing on a random symbol if stop is triggered before API response)
        if (this.pendingLandingSymbolId !== null) {
          shouldExecuteStop = true;
          break; // Stop found — exit wrap loop, execute stop below
        }
      }
    }

    this._cullOffscreen();

    if (shouldExecuteStop) {
      this._calculateAndExecuteStop();
    }
  }

  /**
   * Draw only the sprites that can show through the cell window. A sprite is
   * visible when its centre is within one cell of the window centre (the
   * spinning container is offset by -VISIBLE_SYMBOL_INDEX cells, so the
   * window centre is at container y = VISIBLE_SYMBOL_INDEX * height).
   */
  _cullOffscreen() {
    const h = this.symbolHeight;
    const centre = VISIBLE_SYMBOL_INDEX * h;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const c = this.spinningSprites[i].container;
      c.renderable = Math.abs(c.y - centre) < h;
    }
  }

  /**
   * The landing's sound, at the frame it lands (ReelControllerV2's moment).
   * A blank lands silently; anything else is a coin_land, once per frame for
   * the whole grid so a row landing together is one hit, not five.
   */
  _playLandingSound() {
    if (this._isManualStop) return;
    const id = this.symbolId;
    if (id === null || id === undefined || id === BLANK_SYMBOL_ID) return;
    if (!symbolData[id]) return;
    soundManager.playOncePerFrame(SOUND_NAME.coin_land);
  }

  // ── Wrap (from ReelControllerV2) ──────────────────────────────────────

  // Wraps last sprite to top — single-sprite teleport (no grid-snap on other sprites)
  _moveLastSymbolOnTop() {
    const movedSymbol = this.spinningSprites.pop();
    this.spinningSprites.unshift(movedSymbol);

    // Teleport only the wrapped sprite to the top position.
    // Other sprites keep their exact Y positions — no grid-snap stutter.
    const totalStripHeight = SPRITE_COUNT * this.symbolHeight;
    movedSymbol.container.y -= totalStripHeight;
  }

  _getWrapThreshold() {
    if (
      this._wrapThresholdCache === null ||
      this._lastSymbolHeightForWrapThreshold !== this.symbolHeight
    ) {
      this._wrapThresholdCache = SPRITE_COUNT * this.symbolHeight;
      this._lastSymbolHeightForWrapThreshold = this.symbolHeight;
    }
    return this._wrapThresholdCache;
  }

  // ── Stop Calculation (from ReelControllerV2) ──────────────────────────

  _calculateAndExecuteStop() {
    if (this.hasStopped || this.stopTweenActive) {
      return;
    }
    this.stopTweenActive = true;
    this._landingPeakFired = false; // Reset for this landing

    // Set landing texture on the landing zone sprite
    this._setLandingTexture();

    // Calculate drift compensation
    const currentDrift = this._calculateCurrentDrift();
    const baseLandingDistance = STOP_MOVE_DISTANCE * this.symbolHeight;
    let adjustedLandingDistance = baseLandingDistance - currentDrift;

    const speedConfig = this._getSpeedConfig();
    // Manual slam: N=1 travel can collapse to ~0 after drift — floor distance so
    // the symbol always drops from above. No duration floor (that caused empty crawl).
    if (this._isManualStop) {
      const minFrac = speedConfig.MIN_STOP_DISTANCE_FRACTION_MANUAL ?? 0.75;
      const minDistance = this.symbolHeight * minFrac;
      if (adjustedLandingDistance < minDistance) {
        adjustedLandingDistance = minDistance;
      }
    }

    // Manual stop: use snappier overshoot for more responsive feel
    const overshoot = (this._isManualStop && speedConfig.BOUNCE_OVERSHOOT_MANUAL != null)
      ? speedConfig.BOUNCE_OVERSHOOT_MANUAL
      : speedConfig.BOUNCE_OVERSHOOT;

    // Calculate stop duration — use LANDING_DURATION (fixed) when available,
    // otherwise fall back to velocity-matched calc
    let stopDuration;
    const landingDuration = speedConfig.LANDING_DURATION || null;
    const exitSpeed = speedConfig.LANDING_EXIT_SPEED ?? null;
    this._stopHermite = false;
    if (exitSpeed != null) {
      // ── HANDING OVER TO THE LANDING CLIP (SymbolStripSpinner's landing) ──
      //
      // Every ease-out arrives at zero speed, so the cell stopped dead and the
      // symbol's `in` clip then started it moving again from a standstill —
      // two motions with a full stop between them. Instead the landing is a
      // cubic Hermite from the spin's velocity to the clip's OPENING velocity,
      // so the cell is still travelling at exactly the speed `in` continues at.
      // With both ends fixed the duration is arithmetic, not taste:
      //
      //   2 x distance / (v0 + v1)
      //
      // Pixels per ms at both ends (rescaled on resize).
      const v0 =
        (this.normalizedSpinSpeed / 1000) *
        (this._tensionDecelerating ? this._tensionSpeedMultiplier : 1.0);
      const v1 = exitSpeed * this.symbolHeight;
      this._stopEntryPx = v0;
      this._stopExitPx = v1;
      this._stopHermite = true;
      stopDuration = (2 * adjustedLandingDistance) / (v0 + v1);
    } else if (landingDuration) {
      stopDuration = landingDuration;
    } else {
      // Velocity-matched: landing adapts to current spin speed (including tension deceleration)
      // Matches ReelControllerV2 — when tension is active, _tensionSpeedMultiplier reduces
      // effectiveVelocity, which naturally produces a longer, gentler landing.
      const effectiveVelocity = this.normalizedSpinSpeed
        * (this._tensionDecelerating ? this._tensionSpeedMultiplier : 1.0);
      const velocityMatchedDuration =
        ((overshoot + 3) * adjustedLandingDistance * 1000) / effectiveVelocity;
      const slowdownFactor = (this._isManualStop && speedConfig.STOP_SLOWDOWN_FACTOR_MANUAL != null)
        ? speedConfig.STOP_SLOWDOWN_FACTOR_MANUAL
        : (speedConfig.STOP_SLOWDOWN_FACTOR || 1.0);
      stopDuration = velocityMatchedDuration * slowdownFactor;

      // Tension landing: stretch duration to compensate for overshoot appearing faster against slow spin.
      // Per-speed override (speedConfig.TENSION_LANDING_SLOWDOWN) takes priority over global config.
      if (this._tensionDecelerating) {
        const tensionLandingSlowdown = speedConfig.TENSION_LANDING_SLOWDOWN
          ?? FEATURES_CONFIG.TENSION?.REEL_DECELERATION?.TENSION_LANDING_SLOWDOWN
          ?? 1.0;
        stopDuration *= tensionLandingSlowdown;
      }
    }

    // Store stop params for the ticker (elapsed accumulates via deltaMS)
    this._stopElapsed = 0;
    this._stopDistance = adjustedLandingDistance;
    this._stopDuration = stopDuration;
    this._stopOvershoot = overshoot;
    this._stopLastEasedPos = 0;

    this.isPlaying = false;
  }

  _setLandingTexture() {
    const landingId =
      this.pendingLandingSymbolId !== null
        ? this.pendingLandingSymbolId
        : this._generateRandomSymbolId();


    // Set on sprite[1] only (the landing zone, above visible) — only at stop start
    // Mark as landing sprite so _setSpriteTexture uses the real API value
    if (this.spinningSprites.length >= 2) {
      this.spinningSprites[1]._isLandingSprite = true;
      this._setSpriteTexture(this.spinningSprites[1], landingId);
      this.spinningSprites[1]._isLandingSprite = false;
    }

    // Track the symbol that will land
    this.symbolId = landingId;
  }

  _calculateCurrentDrift() {
    if (!this.spinningSprites.length) return 0;
    const expectedY = VISIBLE_SYMBOL_INDEX * this.symbolHeight;
    const actualY = this.spinningSprites[VISIBLE_SYMBOL_INDEX].container.y;
    return actualY - expectedY;
  }

  // ── Finalize Landing (from ReelControllerV2) ──────────────────────────

  _finalizeLanding() {
    // No overshoot means no trough, so the peak never fired — the landing's
    // moment is now, at the stop.
    if (!this._landingPeakFired) {
      this._landingPeakFired = true;
      this._playLandingSound();
      if (this.onLandingPeak) this.onLandingPeak(this.reelIndex, this.symbolIndex);
    }

    // Remove ticker
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Clear stop params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    // Reset tension deceleration state (matches ReelControllerV2._finalizeLanding)
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Apply deferred resize before snapping to grid (safe point)
    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    // Rotate array: move last STOP_MOVE_DISTANCE sprites to front (single O(n) operation)
    const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE;
    const tail = this.spinningSprites.splice(cut);
    this.spinningSprites.unshift(...tail);

    // Reset positions to grid
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Set critical state flags immediately
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.currentVelocity = 0;
    this.spinStartTime = null;
    this._spinElapsed = 0;

    // Hide spinning container — the static sprite + overlay spine take over.
    // Using container alpha (not individual sprite.visible) prevents
    // random textures from leaking through transparent symbols.
    this.spinningContainer.alpha = 0;

    // Fire stop callback to the spin engine
    if (this.onStopCallback) {
      this.onStopCallback(this.symbolIndex, this.reelIndex);
    } else {
    }
  }

  // ── Resize (deferred during animation — ported from ReelControllerV2) ────
  //
  // When spinning, stopping, or winding up, resize is DEFERRED to safe points
  // (wrap boundary, stop easing rescale, finalize landing, next spin start).
  // This prevents corrupted stop easing distances and sprite position desync
  // that cause the spin to get lost on orientation flip / window resize.
  //
  // When idle (not animating), resize is applied immediately.

  resize(symbolWidth, symbolHeight) {
    // ── A size of 0 or NaN is not a size ──
    // ReelControllerV2.resizeSymbol's guard. Taken as a real size it set
    // symbolHeight to 0, the next deferred resize divided by it, every sprite
    // position became NaN, and the cell never landed.
    if (!(symbolWidth > 0) || !(symbolHeight > 0)) return;

    // No change — skip
    if (this.symbolHeight === symbolHeight && this.symbolWidth === symbolWidth) {
      this._pendingPositionUpdate = false;
      return;
    }

    this._targetSymbolHeight = symbolHeight;
    this._targetSymbolWidth = symbolWidth;

    // If animating: defer update to avoid mid-animation position chaos
    // isStopping covers the gap between stopSpin() and _calculateAndExecuteStop()
    const isAnimating = (this.isPlaying || this.isStopping || this.stopTweenActive || this.isWindingUp) && !this.hasStopped;
    if (isAnimating) {
      this._originalSymbolHeight = this.symbolHeight;
      this._pendingPositionUpdate = true;
      return;
    }

    // Not animating: update immediately
    this._originalSymbolHeight = this.symbolHeight;
    this._pendingPositionUpdate = false;

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    // Invalidate caches
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear();
    this._speedConfigCache = null;

    // Reset to grid positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Rescale all sprite textures + text
    this._rescaleAllTextures();
  }

  /**
   * Apply a deferred resize — called at safe points during animation.
   * Proportionally scales sprite positions and recalculates speed so the
   * animation continues smoothly at the new dimensions.
   * Ported from ReelControllerV2._applyPendingResize().
   */
  _applyPendingResize() {
    if (!this._pendingPositionUpdate) return;

    const oldSymbolHeight = this._originalSymbolHeight || this.symbolHeight;
    const newSymbolHeight = this._targetSymbolHeight;
    if (!(newSymbolHeight > 0) || !(this._targetSymbolWidth > 0)) {
      this._pendingPositionUpdate = false;
      return;
    }
    // A usable ratio needs a real old height too; otherwise snap to the grid.
    const ratioUsable = oldSymbolHeight > 0;
    const scaleRatio = ratioUsable ? newSymbolHeight / oldSymbolHeight : 1;
    const isSpinning = ratioUsable && this.spinStartTime && !this.hasStopped;

    // Update dimensions
    this.symbolHeight = this._targetSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;

    // Invalidate caches
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear();
    this._speedConfigCache = null;

    if (isSpinning && Math.abs(scaleRatio - 1.0) > 0.0001) {
      // ── Proportional mid-spin scaling ──
      // Scale sprite positions to match new symbolHeight — prevents discontinuous jump.
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y =
          Math.round(this.spinningSprites[i].container.y * scaleRatio * 100000) / 100000;
      }

      // Scale lastCalculatedPosition — critical to prevent movement jump next frame
      this.lastCalculatedPosition =
        Math.round(this.lastCalculatedPosition * scaleRatio * 100000) / 100000;

      // Recalculate spin speed with new symbolHeight
      const speedConfig = this._getSpeedConfig();
      this.normalizedSpinSpeed =
        (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    } else if (!isSpinning) {
      // Not spinning — reset to grid positions
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }

    // Rescale all sprite textures + text
    this._rescaleAllTextures();

    // Update spinning container offset to match new symbolHeight
    // (VISIBLE_SYMBOL_INDEX = 2, so offset = -2 * symbolHeight)
    if (this.spinningContainer) {
      this.spinningContainer.y = -2 * this.symbolHeight;
    }

    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = this.symbolHeight;
  }

  /**
   * Rescale all spinning sprite textures + coin value text to current dimensions.
   * Extracted to avoid duplication between resize() and _applyPendingResize().
   */
  _rescaleAllTextures() {
    // Re-applying the texture re-applies the label too, at the new size.
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];

      if (spriteData.currentSymbolId !== null) {
        const currentId = spriteData.currentSymbolId;
        spriteData.currentSymbolId = null;
        this._setSpriteTexture(spriteData, currentId);
      }
    }
  }

  // ── Tension Deceleration (ported from ReelControllerV2) ───────────────

  /**
   * Enter tension mode: gradually decelerate from full speed to TARGET_SPEED.
   * The deceleration is applied as a speed multiplier in updateSymbolsAbsolute().
   * Ported directly from ReelControllerV2.spinTension().
   */
  spinTension() {
    if (!this.isPlaying || this.isStopping || this.hasStopped) return;

    const stConfig = FEATURES_CONFIG.HOLD_AND_WIN_SPIN?.SYMBOL_TENSION;
    if (!stConfig?.ENABLED) return;

    this.isTensionMode = true;

    const tensionDecel = FEATURES_CONFIG.TENSION?.REEL_DECELERATION;
    if (tensionDecel?.ENABLED) {
      // Use symbol tension duration from config (matches the SpinEngine's scheduled stop timer)
      const cycleDuration = stConfig.DURATION ?? 3500;
      this._tensionDecelerationDuration = cycleDuration * (tensionDecel.DECELERATION_RATIO ?? 1.0);
      this._tensionDecelerating = true;
      this._tensionDecelElapsed = 0;
      this._tensionSpeedMultiplier = 1.0;
    }
  }

  /**
   * Stop tension mode spin — triggers normal stop.
   * Keeps isTensionMode=true so _finalizeLanding knows this was a tension stop.
   */
  stopSpinTension() {
    if (this.isStopping || this.hasStopped) return;
    if (!this.isPlaying && !this.isWindingUp) return;
    this.isStopping = true;
    this.isTensionMode = true;
  }

  /**
   * Reset tension deceleration state (called on cleanup / new spin).
   */
  _resetTensionState() {
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionDecelerationDuration = 0;
    this._tensionSpeedMultiplier = 1.0;
    this.isTensionMode = false;
  }

  // ── Pause/Resume for tab switching — NO-OPS (matches ReelControllerV2) ──
  // AppActivityContext sets PIXI.Ticker.shared.speed = 0 on tab hide,
  // which makes deltaMS = 0 for all subscribers. Spin/stop/wind-up and
  // TickerTimeouts freeze automatically; no wall-clock compensation needed.
  pauseSpin() {}
  resumeSpin() {}

  // ── Freeze Spin (from ReelControllerV2) ────────────────────────────────
  // Instantly stops all animations without landing. Used for error popups
  // where cells should freeze at current position.

  freezeSpin() {
    // Stop ticker immediately
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop wind-up if active
    if (this._windUpTickerFn) {
      PIXI.Ticker.shared.remove(this._windUpTickerFn);
      this._windUpTickerFn = null;
      this.isWindingUp = false;
    }

    // Clear ticker-based stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this._landingPeakFired = false;

    // Set state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.spinStartTime = null;
    this._spinElapsed = 0;
    this.currentVelocity = 0;
    this.accelerationMultiplier = 1.0;

    // Reset tension state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // DO NOT hide spinningContainer — keep sprites visible at their frozen positions.
    // Unlike _finalizeLanding() where the static sprite takes over, during a freeze
    // the static sprite is already hidden (set to alpha=0.001 at spin start).
    // Hiding spinning container too would make the cell completely invisible.
    // Matches ReelControllerV2.freezeSpin() which never touches container visibility.
  }

  // ── Cleanup ───────────────────────────────────────────────────────────

  cleanup() {
    if (this._windUpTickerFn) {
      PIXI.Ticker.shared.remove(this._windUpTickerFn);
      this._windUpTickerFn = null;
      this.isWindingUp = false;
    }
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (spriteData.container.parent) {
        spriteData.container.parent.removeChild(spriteData.container);
      }
      spriteData.container.destroy({ children: true });
    }
    this.spinningSprites = [];
    this._nativeDimensionsCache.clear();
  }
}

export default HoldAndWinCellController;

